import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import moment, { Moment } from 'moment-timezone'
import React, { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import AmountInputField from 'src/components/AmountInputField/AmountInputField'
import Box from 'src/components/Box/Box'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import CreditCardAndAmountInputBox from 'src/components/CreditCardAndAmountInputBox/CreditCardAndAmountInputBox'
import {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import CurrencyError from 'src/components/CurrencyError/CurrencyError'
import ForeverOrActiveUntilBox from 'src/components/ForeverOrActiveUntilBox/ForeverOrActiveUntilBox'
import NameInputBox from 'src/components/NameInputBox/NameInputBox'
import NotesInputBox from 'src/components/NotesInputBox/NotesInputBox'
import RecurrenceForm, {
  RecurrenceFormValues,
} from 'src/components/RecurrenceForm/RecurrenceForm'
import UserAndNameInputBox from 'src/components/UserAndNameInputBox/UserAndNameInputBox'
import {
  UserDropdownInfiniteScrollProps,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import UserInputBox from 'src/components/UserInputBox/UserInputBox'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { ExtendCurrency } from 'src/enums'
import {
  FormFieldsValidation,
  useFormFields,
  useScrollToScrollGroupRefs,
} from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { BudgetRequestStatus } from 'src/models/budgetRequest.models'
import {
  RecurrencePeriod,
  RecurrenceTerminator,
} from 'src/models/recurrence.models'
import { UserAssociation, UserListModel } from 'src/models/user.models'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { LocationState } from 'src/redux/createRootReducer'
import {
  selectNullSafeCurrencyFromState,
  selectNullSafeLocaleFromState,
} from 'src/redux/selectors/auth.selectors'
import { extendDay } from 'src/utils/date.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { momentIsEmpty } from 'src/utils/validation/moment.validation'
import {
  numberIsBelowMin,
  numberIsEmpty,
} from 'src/utils/validation/number.validation'
import {
  stringArrayIsEmpty,
  stringIsEmpty,
  stringLengthIsLessThan,
} from 'src/utils/validation/string.validation'
import styled, { css, CSSProp } from 'styled-components'

const StyledDiv = styled.div<{
  isDisabled?: boolean
}>`
  &:nth-child(n + 2) {
    margin-top: ${styleConstants.heights.minorInternalGutter};
  }

  ${getBreakpointDown(breakpointSizes.XSM)(css`
    &:nth-child(n + 2) {
      margin-top: ${styleConstants.heights.rightFlyoutPaddingXSMDown};
    }
  `)}

  ${({ isDisabled }): CSSProp => {
    return isDisabled
      ? css`
          opacity: 0.5;
          pointer-events: none;
        `
      : css``
  }}
`

export type BudgetFlatFormValues = {
  displayName?: string
  email?: string
  emails?: string[]
  creditCardId?: string
  issuingLimitDollars?: number
  untilMoment?: Moment
  isUnlimited: boolean
  isForever: boolean
  currency?: ExtendCurrency
  notes?: string
  parentCreditCardId?: string
}

interface BudgetFormProps {
  className?: string
  initialFlatFields?: Partial<BudgetFlatFormValues>
  initialRecurrenceFields?: Partial<RecurrenceFormValues>
  updateProps?: {
    baseFlatFields: BudgetFlatFormValues
    baseRecurrenceFields?: Partial<RecurrenceFormValues>
    timezone: string
  }
  // TODO: refactor submit fn so that we dont need to pass in recurrence fields and can handle status
  onSuccessfulSubmit: (
    flatFields: BudgetFlatFormValues,
    recurrence: RecurrenceFormValues,
    status: BudgetRequestStatus,
  ) => void
  loading: boolean
  buttonText: React.ReactNode
  topText?: React.ReactNode
  userOptions: UserOption[]
  recentUserOptions?: UserOption[]
  userFieldLabel: React.ReactNode
  rcnOptions?: CreditCardOption[]
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  currencyError?: boolean
  // features
  isFormDisabled: boolean
  recurrenceEditable: boolean
  userInfiniteScrollProps?: UserDropdownInfiniteScrollProps
  creditCardInfiniteScrollProps?: CreditCardDropdownInfiniteScrollProps
  suggestedProps?: {
    showSuggested: boolean | undefined
    showOwnUserInSuggested?: boolean
  }
  filterUserbyAssocationTypes?: UserAssociation[]
  selectedUsers?: UserListModel[]
  unableToAddNewUser?: boolean
  notesEditable?: boolean
  isRequestBudget?: boolean
  isUpdateRequest?: boolean
  isApproveRequest?: boolean
  status?: BudgetRequestStatus
  approver?: UserListModel
  fetchSuggestedUsers?: boolean
}

const BudgetForm: React.FC<BudgetFormProps> = (props): React.ReactNode => {
  const translate = useTranslate()

  const defaultCurrency = useSelector(selectNullSafeCurrencyFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const location = useLocation<LocationState>()
  const NOTES_CHARACTER_LIMIT = 1000
  const [notesCharacters, setNotesCharacters] = useState<number>(
    props.updateProps?.baseFlatFields?.notes?.length ?? 0,
  )
  const [budgetStatus, setBudgetStatus] = useState<BudgetRequestStatus>(
    BudgetRequestStatus.Pending,
  )
  const showRequestBudget = !!props.isRequestBudget
  const isDisabled = !!props.isFormDisabled && !showRequestBudget
  const isUpdateRequest = props.isUpdateRequest
  const isApproveRequest = props.isApproveRequest
  const user = useCurrentUser()
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const isUpdateForm = props.updateProps !== undefined || !!isUpdateRequest

  const {
    fields: flatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
    setErrorsAndReturnDidPassValidation: flatFieldsSetErrors,
    topmostScrollGroup: flatFieldsTopmostScrollGroup,
    setFields: setFlatFields,
  } = useFormFields<BudgetFlatFormValues>(
    {
      displayName: undefined,
      emails: location.state?.autofill
        ? [location.state.autofill]
        : isUpdateForm
          ? undefined
          : [],
      creditCardId: undefined,
      issuingLimitDollars: 0,
      untilMoment: moment().add(1, 'month'),
      isUnlimited: false,
      isForever: false,
      currency: defaultCurrency,
      notes: undefined,
      ...props.initialFlatFields,
    },
    (
      fields: BudgetFlatFormValues,
    ): FormFieldsValidation<BudgetFlatFormValues> => {
      return showRequestBudget || isApproveRequest
        ? {
            displayName: [stringIsEmpty()],
            email: [stringIsEmpty()],
            creditCardId:
              props.isRequestBudget ||
              budgetStatus === BudgetRequestStatus.Rejected
                ? []
                : [stringIsEmpty()],
            issuingLimitDollars: !fields.isUnlimited
              ? [numberIsEmpty(), numberIsBelowMin(1)]
              : [],
            untilMoment:
              props.recurrenceEditable || fields.isForever
                ? undefined
                : [momentIsEmpty()],
            currency: [stringIsEmpty()],
            notes: [stringLengthIsLessThan(1000)],
          }
        : {
            displayName: [stringIsEmpty()],
            emails: !isUpdateForm ? [stringArrayIsEmpty] : [], // TODO: remove optional validation after feature flags are removed
            creditCardId: props.isRequestBudget ? [] : [stringIsEmpty()],
            issuingLimitDollars: !fields.isUnlimited
              ? [numberIsEmpty(), numberIsBelowMin(1)]
              : [],
            untilMoment:
              props.recurrenceEditable || fields.isForever
                ? undefined
                : [momentIsEmpty()],
            currency: [stringIsEmpty()],
            notes: [stringLengthIsLessThan(1000)],
          }
    },
    props.errorDetailMap,
    undefined,
    {
      displayName: 0,
      emails: 1,
      creditCardId: 2,
      issuingLimitDollars: 2,
      currency: 2,
      untilMoment: 3,
    },
  )

  const {
    fields: recurrenceFields,
    fieldsErrors: recurrenceFieldsErrors,
    onFieldChange: onRecurrenceFieldChange,
    setErrorsAndReturnDidPassValidation: recurrenceFieldsSetErrors,
    topmostScrollGroup: recurrenceFieldsTopmostScrollGroup,
  } = useFormFields<RecurrenceFormValues>(
    {
      amountDollars:
        flatFields.issuingLimitDollars !== undefined
          ? flatFields.issuingLimitDollars
          : props.initialRecurrenceFields?.amountDollars !== undefined
            ? props.initialRecurrenceFields.amountDollars
            : 0,
      period:
        props.initialRecurrenceFields?.period !== undefined
          ? props.initialRecurrenceFields.period
          : RecurrencePeriod.Weekly,
      interval:
        props.initialRecurrenceFields?.interval !== undefined
          ? props.initialRecurrenceFields.interval
          : 1,
      terminator:
        props.initialRecurrenceFields?.terminator !== undefined
          ? props.initialRecurrenceFields.terminator
          : RecurrenceTerminator.Count,
      count:
        props.initialRecurrenceFields?.count !== undefined
          ? props.initialRecurrenceFields.count
          : 10,
      untilMoment:
        props.initialRecurrenceFields?.untilMoment !== undefined
          ? props.initialRecurrenceFields.untilMoment
          : moment().add(1, 'month'),
      byWeekDay:
        props.initialRecurrenceFields?.byWeekDay !== undefined
          ? props.initialRecurrenceFields?.byWeekDay
          : extendDay(),
      byMonthDay:
        props.initialRecurrenceFields?.byMonthDay !== undefined
          ? props.initialRecurrenceFields?.byMonthDay
          : moment().date(),
      byYearDay:
        props.initialRecurrenceFields?.byYearDay !== undefined
          ? props.initialRecurrenceFields?.byYearDay
          : moment().date(),
    },
    props.recurrenceEditable
      ? (fields): FormFieldsValidation<RecurrenceFormValues> => ({
          period: [],
          interval: [numberIsEmpty(), numberIsBelowMin(1)],
          terminator: [],
          count:
            fields.terminator === RecurrenceTerminator.Count
              ? [numberIsEmpty(), numberIsBelowMin(1)]
              : [],
          untilMoment: [],
          byWeekDay: [],
          byMonthDay: [],
        })
      : (): FormFieldsValidation<RecurrenceFormValues> => ({}),
    undefined,
    undefined,
    {
      period: 1,
      interval: 1,
      terminator: 1,
      count: 1,
      untilMoment: 1,
      byWeekDay: 1,
      byMonthDay: 1,
    },
  )

  // TODO cleanup boxRef variable names after EnforcingOrgsForBudgets release
  const boxRef0 = useRef<HTMLDivElement>(null)
  const boxRef0b = useRef<HTMLDivElement>(null)
  const boxRef1 = useRef<HTMLDivElement>(null)
  const boxRef2 = useRef<HTMLDivElement>(null)
  useScrollToScrollGroupRefs(
    [boxRef0, boxRef0b, boxRef1, boxRef2],
    [flatFieldsTopmostScrollGroup, recurrenceFieldsTopmostScrollGroup],
  )

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    const flatFieldsPassedValidation = flatFieldsSetErrors()
    const recurrenceFieldsPassedValidation = recurrenceFieldsSetErrors()
    if (flatFieldsPassedValidation && recurrenceFieldsPassedValidation) {
      props.onSuccessfulSubmit(flatFields, recurrenceFields, budgetStatus)
    }
  }

  return (
    <form className={props.className} onSubmit={onSubmit}>
      {props.currencyError
        ? isApproveRequest
          ? props.initialFlatFields?.currency !== undefined && (
              <CurrencyError currency={props.initialFlatFields?.currency} />
            )
          : props.updateProps?.baseFlatFields.currency !== undefined && (
              <CurrencyError
                currency={props.updateProps.baseFlatFields?.currency}
              />
            )
        : undefined}
      {props.topText !== undefined && !props.currencyError && (
        <span
          css={css`
            ${getFontCss(
              12,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          {props.topText}
        </span>
      )}
      {isDisabled && (
        <span
          css={css`
            ${getFontCss(
              12,
              styleConstants.colors.red,
              styleConstants.fontFamilies.medium,
            )};
          `}
        >
          {translate(
            'You do not have an active account to create this budget.',
          )}
        </span>
      )}
      {showRequestBudget ? (
        <StyledDiv ref={boxRef0}>
          <UserAndNameInputBox
            id={'recipient'}
            displayNameProps={{
              baseValue: props.updateProps?.baseFlatFields.displayName,
              showUpdates: isUpdateForm,
              value: flatFields.displayName,
              errors: flatFieldsErrors.displayName,
              onChange: (val: string): void => {
                onFlatFieldChange('displayName', val)
              },
            }}
            displayNameFieldLabel={translate('Budget Name')}
            userEmailProps={{
              value: props.approver?.email
                ? [props.approver.email]
                : flatFields.email
                  ? [flatFields.email]
                  : undefined,
              errors: flatFieldsErrors.email,
              onChange: (val: string[]): void => {
                onFlatFieldChange('email', val[0])
              },
              isStatic: !!isUpdateForm,
            }}
            userFieldLabel={props.userFieldLabel}
            userOptions={props.userOptions}
            recentUserOptions={props.recentUserOptions}
            userInfiniteScrollProps={props.userInfiniteScrollProps}
            suggestedProps={props.suggestedProps}
            filterUserbyAssocationTypes={props.filterUserbyAssocationTypes}
            unableToAddNewUser={props.unableToAddNewUser}
            selectedUsers={props.approver ? [props.approver] : []}
            fetchSuggestedUsers={props.fetchSuggestedUsers}
          />
        </StyledDiv>
      ) : (
        <>
          <StyledDiv isDisabled={isDisabled} ref={boxRef0}>
            <NameInputBox
              fieldLabel={translate('Budget Name')}
              placeholder={translateSingleString(
                locale,
                "What's this budget used for?",
              )}
              baseValue={props.updateProps?.baseFlatFields.displayName}
              showUpdates={isUpdateForm}
              value={flatFields.displayName}
              errors={flatFieldsErrors.displayName}
              onChange={(val: string): void => {
                onFlatFieldChange('displayName', val)
              }}
              testId={'budget-name'}
            />
          </StyledDiv>
          {(!isUpdateForm || isApproveRequest) && (
            <StyledDiv isDisabled={isDisabled} ref={boxRef0b}>
              <UserInputBox
                id={'budgetUsers'}
                userEmailProps={{
                  value: flatFields.emails,
                  errors: flatFieldsErrors.emails,
                  onChange: (val: string[]): void => {
                    onFlatFieldChange('emails', val)
                  },
                  isStatic: props.isApproveRequest,
                }}
                isMulti={!showRequestBudget}
                userFieldLabel={translate('Budget Users')}
                userOptions={props.userOptions}
                recentUserOptions={props.recentUserOptions}
                userInfiniteScrollProps={props.userInfiniteScrollProps}
                suggestedProps={props.suggestedProps}
                filterUserbyAssocationTypes={props.filterUserbyAssocationTypes}
                selectedUsers={props.selectedUsers}
                fetchSuggestedUsers={props.fetchSuggestedUsers}
                getRecipientWarningMessage={(
                  hasSelectedUsersNotInOrg: boolean,
                ): React.ReactNode => (
                  <>
                    {hasSelectedUsersNotInOrg && (
                      <AlertPanel
                        variant="warning"
                        css={css`
                          margin-top: 20px;
                          padding: 12px 20px;
                        `}
                      >
                        <span
                          css={css`
                            ${getFontCss(
                              12,
                              themeConstants.colors.font_primary,
                              styleConstants.fontFamilies.regular,
                            )};
                          `}
                        >
                          {translate(
                            showGuestRole
                              ? "You've added people outside of your org as budget users. They will be invited as Members, and must accept their org invite in order to access this budget."
                              : "You've added people outside of your org as budget users. They must accept their org invite in order to access this budget.",
                          )}
                        </span>
                      </AlertPanel>
                    )}
                  </>
                )}
                unableToAddNewUser={props.unableToAddNewUser}
                helpText={
                  <span
                    css={css`
                      ${getFontCss(
                        12,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.regular,
                      )};
                      margin-top: 8px;
                    `}
                  >
                    {translate(
                      showGuestRole
                        ? 'Budget users can create and update virtual cards from this budget. Only people within your org (excluding Guest users) can be added as a budget user.'
                        : 'Budget users can create and update virtual cards from this budget. Only people within your org can be a budget user.',
                    )}
                  </span>
                }
                displayPills
                preventGuests={showGuestRole}
              />
            </StyledDiv>
          )}
        </>
      )}
      <></>
      {!showRequestBudget ? (
        <StyledDiv isDisabled={isDisabled} ref={boxRef1}>
          <CreditCardAndAmountInputBox
            id={'account'}
            labelCreditCardsAsAccounts={true}
            creditCardOptions={props.rcnOptions}
            balanceLabel={
              isUpdateForm ? translate('Budget Limit') : translate('Limit')
            }
            creditCardIdProps={{
              showUpdates: isUpdateForm,
              value: flatFields.creditCardId,
              errors: flatFieldsErrors.creditCardId,
              onChange: (creditCardId: string): void => {
                const selectedCreditCard = props.rcnOptions?.find(
                  (option) => option.value === creditCardId,
                )?.details?.creditCard
                setFlatFields({
                  ...flatFields,
                  creditCardId: creditCardId,
                  currency: selectedCreditCard?.currency,
                })
              },
              isStatic: isUpdateForm && !isApproveRequest,
            }}
            balanceDollarsProps={{
              showUpdates: isUpdateForm,
              baseValue: props.updateProps?.baseFlatFields.issuingLimitDollars,
              value: flatFields.issuingLimitDollars,
              errors: flatFieldsErrors.issuingLimitDollars,
              onChange: (val: number | undefined): void => {
                onFlatFieldChange('issuingLimitDollars', val)
                onRecurrenceFieldChange('amountDollars', val)
              },
            }}
            unlimitedAmountProps={
              props.recurrenceEditable
                ? undefined
                : {
                    isUnlimited: flatFields.isUnlimited,
                    setIsUnlimited: (val: boolean): void => {
                      onFlatFieldChange('isUnlimited', val)
                    },
                  }
            }
            recurrenceValues={recurrenceFields}
            recurrenceErrors={recurrenceFieldsErrors}
            recurrenceChangeHandlers={
              props.recurrenceEditable
                ? {
                    onPeriodChange: (val: RecurrencePeriod): void => {
                      onRecurrenceFieldChange('period', val)
                    },
                    onIntervalChange: (val: number): void => {
                      onRecurrenceFieldChange('interval', val)
                    },
                    onTerminatorChange: (val: RecurrenceTerminator): void => {
                      onRecurrenceFieldChange('terminator', val)
                    },
                    onCountChange: (val: number): void => {
                      onRecurrenceFieldChange('count', val)
                    },
                    onUntilMomentChange: (val: Moment): void => {
                      onRecurrenceFieldChange('untilMoment', val)
                    },
                    onByWeekDayChange: (val: number): void => {
                      onRecurrenceFieldChange('byWeekDay', val)
                    },
                    onByMonthDayChange: (val: number): void => {
                      onRecurrenceFieldChange('byMonthDay', val)
                    },
                  }
                : undefined
            }
            baseRecurrenceValues={props.updateProps?.baseRecurrenceFields}
            timezone={props.updateProps?.timezone}
            maxValue={10000000}
            currencyProps={{
              showUpdates: isUpdateForm,
              baseValue: props.updateProps?.baseFlatFields.currency,
              value: flatFields.currency,
              errors: flatFieldsErrors.currency,
              onChange: (val: string): void => {
                onFlatFieldChange('currency', val)
              },
            }}
            creditCardInfiniteScrollProps={props.creditCardInfiniteScrollProps}
          />
        </StyledDiv>
      ) : (
        <StyledDiv ref={boxRef1}>
          <Box>
            <AmountInputField
              showUpdates={isUpdateForm}
              baseValue={props.updateProps?.baseFlatFields.issuingLimitDollars}
              value={flatFields.issuingLimitDollars}
              onChange={(val: number | undefined): void => {
                onFlatFieldChange('issuingLimitDollars', val)
                onRecurrenceFieldChange('amountDollars', val)
              }}
              errors={flatFieldsErrors.issuingLimitDollars}
              maxValue={10000000}
              currency={flatFields.currency}
              baseCurrency={props.updateProps?.baseFlatFields.currency}
              onCurrencyChange={(val: string): void => {
                onFlatFieldChange('currency', val)
              }}
              isRequest
            />
            {props.recurrenceEditable && (
              <RecurrenceForm
                css={css`
                  margin-top: 32px;
                `}
                recurrenceValues={recurrenceFields}
                recurrenceErrors={recurrenceFieldsErrors}
                recurrenceChangeHandlers={{
                  onPeriodChange: (val: RecurrencePeriod): void => {
                    onRecurrenceFieldChange('period', val)
                  },
                  onIntervalChange: (val: number): void => {
                    onRecurrenceFieldChange('interval', val)
                  },
                  onTerminatorChange: (val: RecurrenceTerminator): void => {
                    onRecurrenceFieldChange('terminator', val)
                  },
                  onCountChange: (val: number): void => {
                    onRecurrenceFieldChange('count', val)
                  },
                  onUntilMomentChange: (val: Moment): void => {
                    onRecurrenceFieldChange('untilMoment', val)
                  },
                  onByWeekDayChange: (val: number): void => {
                    onRecurrenceFieldChange('byWeekDay', val)
                  },
                  onByMonthDayChange: (val: number): void => {
                    onRecurrenceFieldChange('byMonthDay', val)
                  },
                }}
              />
            )}
          </Box>
        </StyledDiv>
      )}
      {!props.recurrenceEditable && (
        <StyledDiv isDisabled={isDisabled} ref={boxRef1}>
          <ForeverOrActiveUntilBox
            validToMomentProps={{
              showUpdates: isUpdateForm,
              baseValue: props.updateProps?.baseFlatFields.untilMoment,
              value: flatFields.untilMoment,
              errors: flatFieldsErrors.untilMoment,
              onChange: (val: Moment | undefined): void => {
                onFlatFieldChange('untilMoment', val)
              },
            }}
            foreverUntilProps={{
              isForever: flatFields.isForever,
              setIsForever: (val: boolean): void => {
                onFlatFieldChange('isForever', val)
              },
            }}
          />
        </StyledDiv>
      )}
      <StyledDiv isDisabled={isDisabled}>
        <NotesInputBox
          notesProps={{
            showUpdates: isUpdateForm,
            baseValue: props.updateProps?.baseFlatFields.notes,
            value: flatFields.notes,
            errors: flatFieldsErrors.notes,
            onChange: (val: string): void => {
              onFlatFieldChange('notes', val)
              setNotesCharacters(val.length)
            },
          }}
          collapsible
          maxLength={NOTES_CHARACTER_LIMIT}
          notesCharacters={notesCharacters}
          id={'notes'}
        />
      </StyledDiv>
      {props.topLevelError !== undefined && (
        <span
          css={css`
            margin-top: 40px;
            ${getFontCss(
              14,
              styleConstants.colors.red,
              styleConstants.fontFamilies.regular,
            )};

            ${getBreakpointDown(breakpointSizes.XSM)(css`
              margin-top: 30px;
            `)};
          `}
        >
          {props.topLevelError}
        </span>
      )}
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          margin-top: 40px;
          ${getBreakpointDown(breakpointSizes.XSM)(css`
            margin-top: 30px;
          `)}
        `}
      >
        <Button
          css={css`
            ${getBreakpointDown(breakpointSizes.XSM)(css`
              margin-top: 30px;
              width: 100%;
            `)};
          `}
          design={ButtonDesigns.designCta}
          type={ButtonTypes.submit}
          big={true}
          text={props.buttonText}
          loading={props.loading}
          disabled={isDisabled}
          id={
            showRequestBudget
              ? 'requestBudget'
              : isApproveRequest
                ? 'approveBudgetRequest'
                : isUpdateForm
                  ? 'updateBudget'
                  : 'createBudget'
          }
          onClick={
            isApproveRequest
              ? (): void => setBudgetStatus(BudgetRequestStatus.Approved)
              : undefined
          }
        />
        {isApproveRequest ? (
          <Button
            css={css`
              ${getBreakpointDown(breakpointSizes.XSM)(css`
                margin-top: 30px;
                width: 100%;
              `)};
            `}
            design={ButtonDesigns.designRed}
            type={ButtonTypes.submit}
            big={true}
            text={translate('Reject Budget Request')}
            loading={props.loading}
            disabled={isDisabled}
            id={'rejectBudgetRequest'}
            onClick={(): void => setBudgetStatus(BudgetRequestStatus.Rejected)}
          />
        ) : isUpdateRequest ? (
          <Button
            css={css`
              ${getBreakpointDown(breakpointSizes.XSM)(css`
                margin-top: 30px;
                width: 100%;
              `)};
            `}
            design={ButtonDesigns.designRed}
            type={ButtonTypes.submit}
            big={true}
            text={translate('Cancel Request')}
            loading={props.loading}
            disabled={isDisabled}
            id={'cancelBudgetRequest'}
            onClick={(): void => setBudgetStatus(BudgetRequestStatus.Cancelled)}
          />
        ) : (
          <></>
        )}
      </div>
    </form>
  )
}

export default BudgetForm
